import {
    Component,
    type ComponentProps,
    type ComponentType,
    createContext,
    type FC,
    type JSX,
    type PropsWithChildren,
    type ReactElement,
    type ReactNode,
    useEffect,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { createRoot, type Root } from "react-dom/client";
import { systemPrint } from "#/Workers/HardwareConnect/HardwareConnectEvents.tsx";
import { type Without } from "../../../types/internal/utility";

// NOTE: If you're looking for the Hardware Connect print it's in
// HardwareConnectEvents.js

export function printWindow(callback?: () => void): void {
    if(typeof window === "undefined") {
        console.log("Unable to print window as the Window doesn't exist");

        return;
    }

    if(typeof callback === "function") {
        const afterPrint = (): void => {
            callback?.();
            window.removeEventListener("afterprint", afterPrint);
        };

        window.addEventListener("afterprint", afterPrint);
    }

    requestAnimationFrame(() => {
        window.print();
    });
}

export const PrintContext = createContext(true);

// Look at the DataSource.tsx file for how to use this interface with Redux
export interface InjectedPrintProps {
    print?: boolean;
}

/**
 * Makes the child visible to the print window
 * NOTE: If child controls its own state those state changes will not be represented in the printed component
 * as this function actually clones the component rather than remounting in the correct stop
 * @param WrappedComponent
 * @returns React.Node
 */
export function withPrint<
    C extends ComponentType<ComponentProps<C> & InjectedPrintProps>,
    WrappedProps = JSX.LibraryManagedAttributes<C, Without<ComponentProps<C>, InjectedPrintProps>>
>(
    WrappedComponent: C
): ComponentType<WrappedProps> {
    return class extends Component<WrappedProps> {
        public static displayName = "WithPrint";
        protected el = document.getElementById("print") as HTMLDivElement;

        public render(): ReactNode {
            const portal = createPortal(
                <WrappedComponent
                    print={true}
                    {...this.props as any}
                />,
                this.el
            );

            // It is done this way because the portal doesn't
            // mount the children quick enough for the snapshot to get taken
            return (
                <PrintContext.Consumer>
                    {(shouldPrint: boolean): ReactNode => (
                        <>
                            <WrappedComponent
                                print={false}
                                {...this.props as any}
                            />
                            {shouldPrint && portal}
                        </>
                    )}
                </PrintContext.Consumer>
            );
        }
    };
}

const PrintContainer: FC<{
    onPrint?: () => void;
    component: ReactElement;
    shopfrontNode: HTMLElement;
    oldPrint: HTMLElement;
    oldDisplay: string;
    root: Root;
    printNode: HTMLElement;
    styles?: string;
    useSystemPrint?: boolean;
}> = ({
    onPrint,
    shopfrontNode,
    oldPrint,
    oldDisplay,
    root,
    component,
    printNode,
    styles,
    useSystemPrint = false,
}) => {
    // Print
    useEffect(() => {
        const cleanup = () => {
            if(onPrint) {
                onPrint();
            }

            setTimeout(() => {
                // Unmount the root node
                root.unmount();

                // Remove the new print element
                shopfrontNode.removeChild(printNode);

                // Restore the old print element back to how it was
                oldPrint.id = "print";
                oldPrint.style.display = oldDisplay;
            }, 100);
        };

        print(cleanup, styles, useSystemPrint);
        // Ignore potentially changing dependencies, this component isn't dynamic
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return component;
};

/**
 * Helper function to handle printing, with window printing for fallback of system print failing
 */
export function print(
    callback?: () => void,
    styling?: string,
    useSystemPrint = true
): void {
    let styleEl: HTMLStyleElement | null = null;

    // Create styling
    if(typeof styling === "string") {
        styleEl = document.createElement("style");

        styleEl.innerHTML = styling;

        document.head.appendChild(styleEl);
    }

    if(styleEl || typeof callback === "function") {
        // Prepare clean-up of print
        const cleanup = () => {
            window.removeEventListener("afterprint", cleanup);

            // Remove styling
            if(styleEl) {
                document.head.removeChild(styleEl);
            }

            callback?.();
        };

        window.addEventListener("afterprint", cleanup);
    }

    if(useSystemPrint) {
        systemPrint(err => {
            if(err) {
                printWindow();
            }
        });
    } else {
        printWindow();
    }
}

export function printComponent(
    component: ReactElement,
    onPrint?: () => void,
    styles?: string,
    systemPrint?: boolean
): void {
    // NOTE: This function doesn't work after an event is fired in the development version of React
    // Bug: https://github.com/facebook/react/issues/16734

    // We can't override the old print in case something else
    // is using it with a portal (such as the receipt) (SHOPFRONT-3129)
    // So store the old reference and rename it
    const oldPrint = document.getElementById("print") as HTMLElement;
    const oldDisplay = oldPrint.style.display;

    oldPrint.style.display = "none";
    oldPrint.id = "old-print";

    const shopfrontNode = document.getElementById("shopfront") as HTMLElement;

    // Create a new print element
    const print = document.createElement("div");

    print.id = "print";

    // Add it to the body (so it gets all the styles)
    shopfrontNode.appendChild(print);

    // Render the component
    const root = createRoot(print);

    root.render(
        <PrintContainer
            component={component}
            root={root}
            shopfrontNode={shopfrontNode}
            oldPrint={oldPrint}
            onPrint={onPrint}
            oldDisplay={oldDisplay}
            printNode={print}
            styles={styles}
            useSystemPrint={systemPrint}
        />
    );
}

export const usePrint = (content: ReactNode): ReactNode => {
    const [ printRef, setPrintRef ] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        setPrintRef(document.getElementById("print") as HTMLDivElement);
    }, []);

    if(!printRef) {
        return null;
    }

    const portal = createPortal(
        content,
        printRef
    );

    return (
        <PrintContext.Consumer>
            {(shouldPrint: boolean): ReactNode => shouldPrint && portal}
        </PrintContext.Consumer>
    );
};

export const PrintWrapper: FC<PropsWithChildren> = ({ children }) => {
    const print = usePrint(children);

    return (
        <>
            {children}
            {print}
        </>
    );
};
